// Client pentru core-api. Access token-ul (15 min) stă doar în memorie; refresh token-ul e într-un cookie
// httpOnly emis de API (Path=/platform/v1/auth, SameSite=Strict), pe care JavaScript-ul nu îl poate citi.
// La încărcarea paginii, sesiunea se reface printr-un refresh care folosește cookie-ul.

export type Problem = {
  status: number;
  code: string;
  title: string;
  detail?: string;
  errors?: Record<string, string>;
  meta?: Record<string, unknown>;
};

export class ApiError extends Error {
  constructor(public problem: Problem) {
    super(problem.detail ?? problem.title);
  }
}

// Semnalează API-ului „modul browser”: refresh token-ul circulă doar prin cookie, nu prin JSON.
const BROWSER = { "X-Requested-With": "shopcore-admin" };

// Sesiunile de suport (30 min, fără refresh) stau în sessionStorage, ca să supraviețuiască unui reload
// al tab-ului; se închid odată cu el.
const IMP_KEY = "shopcore.support-session";

type Stored = { access_token: string; expires_at: string; support?: boolean };

let current: Stored | null = null;

// Curăță sesiunile salvate de versiunile anterioare (refresh token în localStorage).
try {
  localStorage.removeItem("shopcore.session");
} catch {
  /* storage indisponibil */
}

export const session = {
  get(): Stored | null {
    if (current) return current;
    try {
      const s = JSON.parse(sessionStorage.getItem(IMP_KEY) ?? "null") as Stored | null;
      if (s && JSON.parse(atob(s.access_token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))).exp * 1000 > Date.now()) {
        current = s;
      }
    } catch {
      /* fără sesiune de suport */
    }
    return current;
  },
  set(s: { access_token: string; expires_at: string }, support = false) {
    current = { access_token: s.access_token, expires_at: s.expires_at, support };
    try {
      if (support) sessionStorage.setItem(IMP_KEY, JSON.stringify(current));
      else sessionStorage.removeItem(IMP_KEY);
    } catch {
      /* storage indisponibil: sesiunea rămâne doar în memorie */
    }
  },
  clear() {
    current = null;
    try {
      sessionStorage.removeItem(IMP_KEY);
    } catch {
      /* storage indisponibil */
    }
  },
};

let refreshing: Promise<boolean> | null = null;

// Un singur refresh în zbor per tab; între tab-uri, Web Locks serializează refresh-urile, ca două tab-uri
// să nu rotească simultan același token (al doilea folosește cookie-ul deja înlocuit de primul).
export async function refresh(): Promise<boolean> {
  if (current?.support) return false; // sesiune de suport: fără refresh, expiră după 30 min
  refreshing ??= (async () => {
    const run = async () => {
      const res = await fetch("/platform/v1/auth/refresh", { method: "POST", headers: BROWSER });
      if (!res.ok) {
        session.clear();
        return false;
      }
      session.set(await res.json());
      return true;
    };
    return navigator.locks ? navigator.locks.request("shopcore-refresh", run) : run();
  })().finally(() => setTimeout(() => (refreshing = null), 0));
  return refreshing;
}

type Opts = { method?: string; body?: unknown; form?: FormData; headers?: Record<string, string> };

export async function request<T>(path: string, o: Opts = {}, retry = true): Promise<T> {
  const s = session.get();
  const headers: Record<string, string> = { Accept: "application/json", ...BROWSER, ...o.headers };
  if (o.body !== undefined) headers["Content-Type"] = "application/json";
  if (s) headers.Authorization = `Bearer ${s.access_token}`;
  // FormData: browserul pune singur Content-Type-ul multipart, cu boundary.
  const body = o.form ?? (o.body !== undefined ? JSON.stringify(o.body) : undefined);
  const res = await fetch(path, { method: o.method ?? "GET", headers, body });
  if (res.status === 401 && retry && s && (await refresh())) return request<T>(path, o, false);
  if (res.status === 401 && !path.startsWith("/platform/")) {
    session.clear();
    window.dispatchEvent(new Event("auth:logout"));
  }
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError({ status: res.status, code: "unknown", title: res.statusText, ...data });
  return data as T;
}

// claims citește payload-ul JWT (fără verificare: doar pentru UI, API-ul validează semnătura).
export function claims(): { imp?: string; exp?: number } | null {
  const t = session.get()?.access_token;
  if (!t) return null;
  try {
    return JSON.parse(atob(t.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return null;
  }
}

export const admin = {
  get: <T>(p: string) => request<T>(`/admin/v1${p}`),
  post: <T>(p: string, body?: unknown) => request<T>(`/admin/v1${p}`, { method: "POST", body: body ?? {} }),
  patch: <T>(p: string, body: unknown, headers?: Record<string, string>) => request<T>(`/admin/v1${p}`, { method: "PATCH", body, headers }),
  put: <T>(p: string, body: unknown) => request<T>(`/admin/v1${p}`, { method: "PUT", body }),
  del: (p: string) => request<void>(`/admin/v1${p}`, { method: "DELETE" }),
  upload: <T>(p: string, form: FormData) => request<T>(`/admin/v1${p}`, { method: "POST", form }),
};

export const platform = {
  post: <T>(p: string, body: unknown) => request<T>(`/platform/v1${p}`, { method: "POST", body }, false),
  get: <T>(p: string) => request<T>(`/platform/v1${p}`),
};

// Mesaje în română pentru codurile de eroare pe care comerciantul le poate întâlni în mod obișnuit.
const MESSAGES: Record<string, string> = {
  email_not_verified: "Confirmă mai întâi adresa de email a contului (linkul din email sau „Retrimite emailul” din bannerul de sus).",
  rate_limited: "Prea multe încercări. Încearcă din nou puțin mai târziu.",
  already_verified: "Adresa de email este deja confirmată.",
};

export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    if (MESSAGES[e.problem.code]) return MESSAGES[e.problem.code];
    const f = e.problem.errors;
    if (f) return Object.entries(f).map(([k, v]) => `${k}: ${v}`).join(" · ");
    return e.problem.detail ?? e.problem.title;
  }
  return e instanceof Error ? e.message : "Eroare necunoscută";
}

// download salvează un fișier generat de API (ex: factura PDF). Token-ul stă doar în memorie, deci nu putem
// folosi un simplu <a href>: cerem fișierul cu Authorization și îl oferim browserului ca blob.
export async function download(path: string, filename: string): Promise<void> {
  const get = () => fetch(`/admin/v1${path}`, { headers: { ...BROWSER, Authorization: `Bearer ${session.get()?.access_token ?? ""}` } });
  let res = await get();
  if (res.status === 401 && (await refresh())) res = await get();
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new ApiError({ status: res.status, code: "unknown", title: res.statusText, ...data });
  }
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
